import { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';

import Page from '../../components/Page';
import {
  fetchCustomerData,
  getDomain,
  fetchITicketWidgets,
  parseITicketUserData,
} from '../../utils/functions';
import { Customer, Order } from '../../types/customer';
import { iTicketWidget } from '../../types/drupal';
import Spinner from '../../components/Spinner';
import ReservationItem from './ReservationItem';
import '../../styles/ITicketWidgetStyles.css';

interface Props {
  idToken: string;
}

const BookingsPage: React.FC<Props> = (props: Props): JSX.Element => {
  const domain = getDomain();
  const { i18n, t } = useTranslation(['bookings']);
  const currentLanguage = i18n.language;

  const [orders, setOrders] = useState<Order[]>();
  const [customerData, setCustomerData] = useState<Customer>();

  const [iTicketUserParams, setITicketUserParams] = useState<string>();

  const activeBookingsContainer = useRef<HTMLUListElement>(null);
  const [iTicketWidgets, setITicketWidgets] = useState<iTicketWidget[]>();

  const [errorMessage, setErrorMessage] = useState<string | null>();
  const pageId: { [key: string]: string } = {};
  pageId['ruka'] = process.env.REACT_APP_BOOKINGS_PAGE_RUKA ?? '';
  pageId['pyha'] = process.env.REACT_APP_BOOKINGS_PAGE_PYHA ?? '';

  useEffect(() => {
    async function getBookingData() {
      const customerData = await fetchCustomerData(
        props.idToken,
        false,
        'ipsapi,cdp'
      );

      if (customerData?.cdpData) {
        const userDataParams = parseITicketUserData(
          customerData.cdpData.references
        );

        if (userDataParams) {
          setITicketUserParams(userDataParams);
        }
      }

      if (customerData && customerData.orders) {
        setOrders(customerData.orders);
        setCustomerData(customerData.customer);
      } else {
        setErrorMessage(t('error message'));
      }
    }

    if (props.idToken) {
      getBookingData();
    }
  }, [props.idToken]);

  let mainContent;
  let secondaryContent = (mainContent = (
    <div className='py-8'>
      <Spinner />
    </div>
  ));

  const currentTime = new Date(new Date().setHours(0, 0, 0, 0)).getTime();

  if (orders) {
    // Separate reservations by upcoming or past.
    const activeOrders = orders
      .filter((order) => {
        const orderEndTime = new Date(order.DateTimeEnd).getTime();
        if (orderEndTime >= currentTime) {
          return true;
        }
        return false;
      })
      .sort(sortActiveOrders);

    const oldOrders = orders
      .filter((order) => {
        const orderEndTime = new Date(order.DateTimeEnd).getTime();
        if (orderEndTime < currentTime) {
          return true;
        }
        return false;
      })
      .sort(sortPastOrders);

    mainContent = getBookingListElement(activeOrders, false);
    secondaryContent = getBookingListElement(oldOrders, true);
  } else if (errorMessage) {
    mainContent = secondaryContent = <p>{errorMessage}</p>;
  }

  function sortPastOrders(a: Order, b: Order): number {
    if (new Date(a.DateTimeStart) < new Date(b.DateTimeStart)) {
      return 1;
    }
    return -1;
  }

  function sortActiveOrders(a: Order, b: Order): number {
    if (new Date(a.DateTimeStart) > new Date(b.DateTimeStart)) {
      return 1;
    }
    return -1;
  }

  function getBookingListElement(
    orders: Order[],
    oldReservation: boolean
  ): JSX.Element {
    if (orders.length === 0) {
      return <p>{t('No bookings or purchases found', { ns: 'common' })}</p>;
    }

    return (
      <ul ref={!oldReservation ? activeBookingsContainer : null}>
        {orders.map((order, i) => {
          if (!order.product) {
            return null;
          }

          return (
            <ReservationItem
              key={i}
              order={order}
              oldReservation={oldReservation}
              domain={domain}
              customerData={customerData as Customer}
              idToken={props.idToken}
              iTicketWidgets={!oldReservation ? iTicketWidgets : null}
            ></ReservationItem>
          );
        })}
      </ul>
    );
  }

  useEffect(() => {
    if (iTicketWidgets && iTicketWidgets.length > 0) {
      loadITicketWidgets(0);
    }
  }, [iTicketWidgets]);

  function loadITicketWidgets(i = 0) {
    if (!iTicketWidgets || !iTicketWidgets[i]) {
      return;
    }

    const widget = iTicketWidgets[i];
    let widgetUrl = widget.widget[currentLanguage];
    // If user has previously used iticket, the code and username will be added to the
    // widget src, and the user will be automatically logged in.
    if (iTicketUserParams) {
      widgetUrl += `&${iTicketUserParams}`;
    }

    // Generate the script.
    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.async = true;
    script.src = widgetUrl;

    if (!activeBookingsContainer.current) {
      return;
    }

    const widgetPlaceholder = activeBookingsContainer.current.querySelector(
      `#iticket-widget-placeholder-${i}`
    );
    if (!widgetPlaceholder) {
      return;
    }

    // Temporarily set the actual id for the placeholder
    // element that the iTicket script will target.
    const originalId = widgetPlaceholder.id;
    widgetPlaceholder.id = widget.token;

    script.onload = () => {
      if (!activeBookingsContainer.current) {
        return;
      }

      const loadedWidget = activeBookingsContainer.current.querySelector(
        `[id="${widget.token}"]`
      );
      if (!loadedWidget) {
        return;
      }

      // Set back the placeholder id, so we can load the next widget
      // if available. If there are multiple elements with the same id,
      // or loading multiple iTicket widgets won't work.
      loadedWidget.id = originalId;

      // If there are more widgets available, call this function again.
      if (iTicketWidgets.length >= i + 1) {
        loadITicketWidgets(i + 1);
      }
    };
    document.body.appendChild(script);
  }

  // After orders and accommodations have been fetched,
  // fetch iTicket widgets applying for them.
  useEffect(() => {
    async function getITicketWidgets() {
      if (!orders) return;

      const activeOrders = orders
        .filter((order) => {
          const orderEndTime = new Date(order.DateTimeEnd).getTime();
          if (orderEndTime >= currentTime) {
            return true;
          }
          return false;
        })
        .sort(sortActiveOrders);

      const iTicketRequests = activeOrders
        .map((order) => {
          if (!order.product) return null;

          const start = dayjs(order.DateTimeStart).format('YYYY-MM-DD');
          const end = dayjs(order.DateTimeEnd).format('YYYY-MM-DD');
          const resort = order.product.resort === 'Ruka' ? 'ruka' : 'pyha';
          const purchaseDate = dayjs(order.DoneDate).format('YYYY-MM-DD');

          const request = fetchITicketWidgets(
            start,
            end,
            order.product.field_product_id,
            'false',
            props.idToken,
            order.HotellinxReservationLineId,
            resort,
            purchaseDate
          );

          return request;
        })
        .filter((request) => request);

      const response = await Promise.all(iTicketRequests);

      if (response !== null && response.length > 0) {
        const widgets = response.flat() as iTicketWidget[];
        setITicketWidgets(widgets);
      }
    }

    if (props.idToken && orders && orders.length > 0 && customerData) {
      getITicketWidgets();
    }
  }, [orders, props.idToken, customerData]);

  return (
    <div className='bookings'>
      <Page
        pageId={pageId[domain]}
        mainContent={mainContent}
        secondaryContent={secondaryContent}
      />
    </div>
  );
};

export default BookingsPage;
